import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Eye, 
  Trash2, 
  Download,
  Building2,
  MapPin,
  Phone,
  Mail,
  Star,
  TrendingUp,
  Users,
  Package,
  RefreshCw
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import ActionMenu from '../common/ActionMenu';
import HelpButton from '../common/HelpButton';
import { useToast, ToastContainer } from '../common/Toast';
import VendorService from '../../services/vendorService';
import VendorForm from './VendorForm';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const VendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showViewForm, setShowViewForm] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    vendorType: '',
    status: '',
    industry: ''
  });
  const [reloadKey, setReloadKey] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
    limit: 10
  });

  const { toasts, removeToast, success, error: showError } = useToast();

  // Load vendors
  const loadVendors = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        vendorType: filters.vendorType || undefined,
        status: filters.status || undefined,
        industry: filters.industry || undefined
      };

      const response = await VendorService.getVendors(params);
      setVendors(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading vendors:', error);
      setError(error.message);
      showError('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  // Load vendor statistics
  const loadVendorStats = async () => {
    try {
      const response = await VendorService.getVendorStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading vendor stats:', error);
    }
  };

  useEffect(() => {
    loadVendors();
    loadVendorStats();
  }, []);

  useEffect(() => {
    loadVendors(pagination.current);
    loadVendorStats();
  }, [reloadKey, pagination.current]);
  // Handle vendor actions
  const handleAddVendor = () => {
    setSelectedVendor(null);
    setShowForm(true);
  };

  const handleEditVendor = (vendor) => {
    setSelectedVendor(vendor);
    setShowForm(true);
  };

  const handleViewVendor = (vendor) => {
    setSelectedVendor(vendor);
    setShowViewForm(true);
  };

  const handleDeleteVendor = (vendor) => {
    setVendorToDelete(vendor);
    setShowDeleteModal(true);
  };

  const confirmDeleteVendor = async () => {
    try {
      await VendorService.deleteVendor(vendorToDelete._id);
      success('Vendor deleted successfully');
      loadVendors(pagination.current);
      loadVendorStats();
      setShowDeleteModal(false);
      setVendorToDelete(null);
    } catch (error) {
      console.error('Error deleting vendor:', error);
      showError('Failed to delete vendor');
    }
  };

  const handleSaveVendor = async (vendorData) => {
    try {
      if (selectedVendor) {
        await VendorService.updateVendor(selectedVendor._id, vendorData);
        success('Vendor updated successfully');
      } else {
        await VendorService.createVendor(vendorData);
        success('Vendor created successfully');
      }
      setShowForm(false);
      setSelectedVendor(null);
      setReloadKey(prev => prev + 1);
    } catch (error) {
      console.error('Error saving vendor:', error);
      showError('Failed to save vendor');
    }
  };

  const handleExportVendors = async () => {
    try {
      const response = await VendorService.exportVendors('csv');
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vendors.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      success('Vendors exported successfully');
    } catch (error) {
      console.error('Error exporting vendors:', error);
      showError('Failed to export vendors');
    }
  };

  // Define columns for DataTable - Optimized for better layout
  const columns = [
    {
      key: 'vendorId',
      label: 'ID',
      width: '120px',
      render: (value, row) => (
        <div className="p-2" style={{ 
          backgroundColor: '#f8f9fa', 
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <div className="d-flex align-items-center gap-2">
            <Building2 size={14} style={{ color: 'var(--primary-color)' }} />
            <span style={{ 
              fontSize: '12px', 
              fontWeight: '600',
              color: 'var(--primary-color)',
              fontFamily: 'monospace'
            }}>
              {value || row._id?.slice(-8)}
            </span>
          </div>
        </div>
      )
    },
    {
      key: 'name',
      label: 'Vendor Name',
      width: '220px',
      render: (value, row) => (
        <div className="p-2" style={{ 
          backgroundColor: '#ffffff', 
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <div className="d-flex align-items-center gap-2 mb-1">
            <Building2 size={16} style={{ color: 'var(--primary-color)' }} />
            <span className="fw-bold" style={{ fontSize: '15px', color: '#212529' }}>
              {value}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '2px' }}>
            {row.vendorType} • {row.industry || 'No industry'}
          </div>
          {row.primaryContact?.email && (
            <div className="d-flex align-items-center gap-1">
              <Mail size={10} style={{ color: '#6c757d' }} />
              <span style={{ fontSize: '11px', color: '#6c757d' }}>
                {row.primaryContact.email}
              </span>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'vendorType',
      label: 'Type',
      width: '130px',
      render: (value) => {
        const typeConfig = {
          'Supplier': { color: '#059669', bgColor: '#ecfdf5', icon: '📦' },
          'Service Provider': { color: '#3b82f6', bgColor: '#eff6ff', icon: '🔧' },
          'Contractor': { color: '#d97706', bgColor: '#fffbeb', icon: '👷' },
          'Consultant': { color: '#8b5cf6', bgColor: '#f3f0ff', icon: '💼' },
          'Manufacturer': { color: '#dc2626', bgColor: '#fef2f2', icon: '🏭' },
          'Distributor': { color: '#0891b2', bgColor: '#ecfeff', icon: '🚚' },
          'Other': { color: '#6b7280', bgColor: '#f9fafb', icon: '📋' }
        };
        
        const config = typeConfig[value] || typeConfig['Other'];
        
        return (
          <div className="p-2" style={{ 
            backgroundColor: config.bgColor, 
            borderRadius: '8px',
            border: `1px solid ${config.color}20`
          }}>
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '14px' }}>{config.icon}</span>
              <span style={{ 
                fontSize: '13px', 
                fontWeight: '600',
                color: config.color
              }}>
                {value}
              </span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      width: '140px',
      render: (value) => {
        const statusConfig = {
          'Active': { color: '#059669', bgColor: '#ecfdf5', icon: '✓' },
          'Inactive': { color: '#6b7280', bgColor: '#f9fafb', icon: '○' },
          'Suspended': { color: '#d97706', bgColor: '#fffbeb', icon: '⚠' },
          'Blacklisted': { color: '#dc2626', bgColor: '#fef2f2', icon: '🚫' },
          'Pending Approval': { color: '#3b82f6', bgColor: '#eff6ff', icon: '⏳' }
        };
        
        const config = statusConfig[value] || statusConfig['Inactive'];
        
        return (
          <div className="p-2" style={{ 
            backgroundColor: config.bgColor, 
            borderRadius: '8px',
            border: `1px solid ${config.color}20`
          }}>
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '14px' }}>{config.icon}</span>
              <span style={{ 
                fontSize: '13px', 
                fontWeight: '600',
                color: config.color
              }}>
                {value}
              </span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'primaryContact',
      label: 'Contact',
      width: '180px',
      render: (value) => {
        if (!value || !value.name) {
          return (
            <div className="d-flex align-items-center gap-2 p-2" style={{ 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <Users size={14} style={{ color: '#6c757d' }} />
              <span style={{ fontSize: '13px', color: '#6c757d' }}>
                No contact info
              </span>
            </div>
          );
        }
        
        return (
          <div className="p-2" style={{ 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <Users size={14} style={{ color: 'var(--primary-color)' }} />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#212529' }}>
                {value.name}
              </span>
            </div>
            {value.title && (
              <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '2px' }}>
                {value.title}
              </div>
            )}
            {value.email && (
              <div className="d-flex align-items-center gap-1">
                <Mail size={10} style={{ color: '#6c757d' }} />
                <span style={{ fontSize: '11px', color: '#6c757d' }}>
                  {value.email}
                </span>
              </div>
            )}
            {value.phone && (
              <div className="d-flex align-items-center gap-1 mt-1">
                <Phone size={10} style={{ color: '#6c757d' }} />
                <span style={{ fontSize: '11px', color: '#6c757d' }}>
                  {value.phone}
                </span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'performance',
      label: 'Rating',
      width: '140px',
      render: (value) => {
        const rating = value?.overallRating || 0;
        const qualityRating = value?.qualityRating || 0;
        const deliveryRating = value?.deliveryRating || 0;
        
        // Determine rating tier based on overall rating
        const getRatingTier = (rating) => {
          if (rating >= 4.5) return { tier: 'Excellent', color: '#059669', bgColor: '#ecfdf5' };
          if (rating >= 4.0) return { tier: 'Good', color: '#3b82f6', bgColor: '#eff6ff' };
          if (rating >= 3.0) return { tier: 'Average', color: '#d97706', bgColor: '#fffbeb' };
          if (rating >= 2.0) return { tier: 'Poor', color: '#dc2626', bgColor: '#fef2f2' };
          return { tier: 'Very Poor', color: '#6b7280', bgColor: '#f9fafb' };
        };
        
        const ratingTier = getRatingTier(rating);
        
        // Generate star display
        const renderStars = (rating) => {
          const stars = [];
          const fullStars = Math.floor(rating);
          const hasHalfStar = rating % 1 >= 0.5;
          
          for (let i = 0; i < 5; i++) {
            if (i < fullStars) {
              stars.push('★');
            } else if (i === fullStars && hasHalfStar) {
              stars.push('☆');
            } else {
              stars.push('☆');
            }
          }
          return stars.join('');
        };
        
        return (
          <div className="p-2" style={{ 
            backgroundColor: ratingTier.bgColor, 
            borderRadius: '8px',
            border: `1px solid ${ratingTier.color}20`
          }}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <Star size={14} style={{ color: ratingTier.color }} />
              <span style={{ fontSize: '14px', fontWeight: '700', color: ratingTier.color }}>
                {rating.toFixed(1)}
              </span>
            </div>
            <div style={{ 
              fontSize: '10px', 
              fontWeight: '600', 
              color: ratingTier.color,
              backgroundColor: ratingTier.color + '20',
              padding: '2px 6px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: '2px'
            }}>
              {ratingTier.tier}
            </div>
            <div style={{ fontSize: '10px', color: '#6c757d' }}>
              {renderStars(rating)}
            </div>
            {(qualityRating > 0 || deliveryRating > 0) && (
              <div style={{ fontSize: '9px', color: '#6c757d', marginTop: '2px' }}>
                Q: {qualityRating.toFixed(1)} • D: {deliveryRating.toFixed(1)}
              </div>
            )}
          </div>
        );
      }
    }
  ];

  // Show view form instead of main content when viewing vendor details
  if (showViewForm && selectedVendor) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title={`View Vendor Details - ${selectedVendor.name}`}
          description="Read Only View"
        />
        <div className="page-container">
          <ToastContainer toasts={toasts} removeToast={removeToast} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginBottom: '24px' }}>
            <button
              className="btn-outline-cam"
              onClick={() => {
                setShowViewForm(false);
                setSelectedVendor(null);
              }}
            >
              <Eye size={16} />
              Back to List
            </button>
            <button
              className="btn-cam"
              onClick={() => {
                setShowViewForm(false);
                setShowForm(true);
              }}
            >
              <Edit size={16} />
              Edit Vendor
            </button>
          </div>

        {/* View Form Content */}
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body p-4">
                    <VendorForm
                      vendor={selectedVendor}
                      viewMode={true}
                      onCancel={() => {
                        setShowViewForm(false);
                        setSelectedVendor(null);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Vendor Management"
        description="Manage your vendors and suppliers"
      />
      <div className="page-container">
        <ToastContainer toasts={toasts} removeToast={removeToast} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginBottom: '24px' }}>
          <HelpButton />
          <button
            className="btn-outline-cam"
            onClick={handleExportVendors}
          >
            <Download size={16} />
            Export
          </button>
          <button
            className="btn-cam"
            onClick={handleAddVendor}
          >
            <Plus size={16} />
            Add Vendor
          </button>
        </div>

      {/* KPI Cards */}
      {stats && (
        <div className="page-stats-grid">
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-1">
                      {stats.overview.totalVendors || 0}
                    </h3>
                    <p className="page-kpi-label">
                      Total Vendors
                    </p>
                  </div>
                  <div className="page-kpi-icon-container color-1">
                    <Building2 size={24} className="page-kpi-icon color-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-2">
                      {stats.overview.activeVendors || 0}
                    </h3>
                    <p className="page-kpi-label">
                      Active Vendors
                    </p>
                  </div>
                  <div className="page-kpi-icon-container color-2">
                    <TrendingUp size={24} className="page-kpi-icon color-2" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-3">
                      {stats.performance.avgOverallRating?.toFixed(1) || '0.0'}
                    </h3>
                    <p className="page-kpi-label">
                      Avg Rating
                    </p>
                  </div>
                  <div className="page-kpi-icon-container color-3">
                    <Star size={24} className="page-kpi-icon color-3" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="page-stat-card">
            <div className="card h-100 page-kpi-card">
              <div className="card-body page-kpi-body">
                <div className="page-kpi-content">
                  <div className="page-kpi-text">
                    <h3 className="page-kpi-value color-4"
                     style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        lineHeight: '1.3',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        maxWidth: '160px',
                        color: '#1f2937' // dark slate for readability
                    }}
                    title={stats.byIndustry[0]?._id}
                    >
                      {stats.byIndustry[0]?._id || 'N/A'}
                    </h3>
                    <p className="page-kpi-label">
                      Top Industry
                    </p>
                  </div>
                  <div className="page-kpi-icon-container color-4">
                    <Package size={24} className="page-kpi-icon color-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Data Table */}
      <div className="page-tab-content">
        <div className="page-table-container">
          <DataTable
            data={vendors}
            columns={columns}
            title="Vendor Directory"
            searchable={true}
            sortable={true}
            exportable={true}
            pagination={true}
            pageSize={pagination.limit}
            showActions={true}
            showCheckboxes={false}
            onView={handleViewVendor}
            onEdit={handleEditVendor}
            onDelete={handleDeleteVendor}
            searchPlaceholder="Search vendors by name, code, or type..."
            loading={loading}
            emptyMessage="No vendors found. Click 'Add Vendor' to get started."
          />
        </div>
      </div>

      {/* Vendor Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: '800px', maxWidth: '90vw' }}>
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center gap-2">
                <Building2 size={20} />
                {selectedVendor ? 'Edit Vendor' : 'Add New Vendor'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setShowForm(false);
                  setSelectedVendor(null);
                }}
                style={{ 
                  fontSize: '18px',
                  opacity: 0.7,
                  transition: 'opacity 0.2s ease',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.7'}
              >
                ×
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <VendorForm
                vendor={selectedVendor}
                onSave={handleSaveVendor}
                onCancel={() => {
                  setShowForm(false);
                  setSelectedVendor(null);
                }}
              />
            </div>
          </div>
        </div>
      )}


      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Vendor"
        message={`Are you sure you want to delete "${vendorToDelete?.name}"? This action cannot be undone.`}
        onConfirm={confirmDeleteVendor}
        onCancel={() => {
          setShowDeleteModal(false);
          setVendorToDelete(null);
        }}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
      </div>
    </>
  );
};

export default VendorManagement;
